/**
 * =============================================================================
 * API TYPE DEFINITIONS
 * =============================================================================
 *
 * Shared types for API requests and responses.
 * Both the Worker and frontend applications import from this file.
 *
 * ARCHITECTURE REFERENCE: Section 6 - API Specification
 *
 * =============================================================================
 */

import type { PublicLaunchMode } from './launchMode';
import type {
  QuestionStatus,
  AuditAction,
  AuditEntityType,
  MediaModerationStatus,
  MediaAuthorType,
  MediaRejectionReason,
  MediaModerationAction,
  AllowedMimeType,
} from './constants';
import type { ImageModerationPosture } from './mediaSettings';
import type { ReleaseFlags } from './releaseFlags';

// =============================================================================
// WINDOW TYPE DEFINITIONS (P1-29)
// =============================================================================

/**
 * Window type categorization for questions.
 * ARCHITECTURE REFERENCE: Section 6 - API Specification
 */
export type WindowType =
  | 'DOUBLE_HUNG'
  | 'SINGLE_HUNG'
  | 'CASEMENT'
  | 'SLIDING'
  | 'AWNING'
  | 'PICTURE'
  | 'BAY_BOW'
  | 'OTHER';

/**
 * Window type options for UI dropdown.
 * Provides human-readable labels for each window type.
 */
export const WINDOW_TYPE_OPTIONS: { value: WindowType; label: string }[] = [
  { value: 'DOUBLE_HUNG', label: 'Double-Hung' },
  { value: 'SINGLE_HUNG', label: 'Single-Hung' },
  { value: 'CASEMENT', label: 'Casement' },
  { value: 'SLIDING', label: 'Sliding/Gliding' },
  { value: 'AWNING', label: 'Awning/Hopper' },
  { value: 'PICTURE', label: 'Picture/Fixed' },
  { value: 'BAY_BOW', label: 'Bay/Bow' },
  { value: 'OTHER', label: 'Other/Unknown' },
];

// =============================================================================
// BASE RESPONSE TYPES
// =============================================================================

/**
 * Standard successful API response wrapper.
 */
export interface ApiSuccessResponse<T> {
  success: true;
  data: T;
}

/**
 * Standard error API response wrapper.
 * ARCHITECTURE REFERENCE: Section 6.5 - Error Response Format
 */
export interface ApiErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
    requestId?: string;
  };
}

/**
 * Union type for all API responses.
 */
export type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse;

// =============================================================================
// PAGINATION
// =============================================================================

export interface PaginationParams {
  page?: number;
  limit?: number;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// =============================================================================
// PUBLIC API TYPES
// =============================================================================

/**
 * Public question list item (minimal data for list view).
 */
export interface PublicQuestionListItem {
  questionId: string;
  title: string;
  slug: string;
  description: string | null;
  status: string;
  posterName: string;
  createdAt: number;
  replyCount: number;
  /**
   * Bucketed, approximate public view count (BL-REV-P1-20-002 / ADR-017 §3.9).
   * NOT the exact internal `view_count`: counts below a threshold report `0`
   * and the rest are floored to a coarse bucket. Never used for SEO/ranking.
   */
  viewCount: number;
  hasSolution: boolean;
  thumbnailUrl: string | null;
  /** Derived from durable correction state; carries no provenance internals. */
  editoriallyCorrected: boolean;
}

/**
 * Response for GET /api/public/questions
 */
export interface QuestionListResponse {
  questions: PublicQuestionListItem[];
  pagination: PaginationMeta;
}

/**
 * Public image attachment on a question or reply (ADR-022 §6.1 public
 * projection). PUBLIC-only DTO: it deliberately carries NO `filename` /
 * `originalFilename` — the uploaded name is user-controlled and may hold PII or
 * abuse, so it is never in a public projection or a public accessible label
 * (ADR-022 §6.1/§9, G9). Admin surfaces use their own media DTO where the
 * filename is legitimate for a moderator.
 *
 * `thumbnailUrl` is the versioned `/…/thumb` variant URL; `url` is the full
 * image (both share the same `public_version`, so a takedown 404s them at
 * origin). `width`/`height` are the persisted intrinsic dimensions.
 */
export interface ImageAttachment {
  attachmentId: string;
  url: string;
  thumbnailUrl?: string;
  /**
   * Validated stored MIME type (ADR-022 §6.1 public projection field). Present
   * on public question-detail payloads; omitted where not projected. Never
   * derived from a user-supplied value.
   */
  mimeType?: string;
  /** Approximate file size in megabytes (2dp), for layout/preview hints. */
  sizeMb?: number;
  /**
   * Intrinsic dimensions in pixels (ADR-022 §8), persisted at upload from the
   * stored bytes. Emitted TOGETHER or not at all: both present → the renderer
   * reserves an aspect-ratio box to prevent layout shift (CLS); absent (legacy
   * pre-dimension rows, ADR-022 §8) → the renderer falls back to a neutral
   * reserved box. A consumer must treat either being missing as "unknown".
   */
  width?: number;
  height?: number;
}

/**
 * Public reply data.
 */
export interface PublicReply {
  replyId: string;
  authorName: string;
  userType: 'ADMIN' | 'CUSTOMER' | 'GUEST';
  body: string;
  replyType: string;
  isSolution: boolean;
  recommendedProducts: RecommendedProduct[];
  createdAt: number;
  images: ImageAttachment[];
  /** Display-safe public editorial-correction marker (ADR-032). */
  editoriallyCorrected: boolean;
}

/**
 * Recommended product in a solution reply.
 */
export interface RecommendedProduct {
  entityId: number;
  name: string;
  url: string;
  imageUrl?: string;
  price?: {
    value: number;
    currencyCode: string;
  };
}

/**
 * Full public question detail.
 */
export interface PublicQuestionDetail {
  questionId: string;
  title: string;
  slug: string;
  description: string | null;
  status: string;
  posterName: string;
  tags: string[];
  createdAt: number;
  resolvedAt: number | null;
  /**
   * Bucketed, approximate public view count (BL-REV-P1-20-002 / ADR-017 §3.9).
   * NOT the exact internal `view_count`: counts below a threshold report `0`
   * and the rest are floored to a coarse bucket. Never used for SEO/ranking.
   */
  viewCount: number;
  replyCount: number;
  images: ImageAttachment[];
  /** Display-safe public editorial-correction marker (ADR-032). */
  editoriallyCorrected: boolean;
}

/**
 * Suggested product in the question detail response.
 * Fetched from BigCommerce catalog based on suggestedProductIds.
 */
export interface SuggestedProduct {
  productId: number;
  name: string;
  sku: string;
  price: number;
  salePrice?: number;
  url: string;
  thumbnailUrl?: string;
  imageUrl?: string;
  isAvailable: boolean;
}

/**
 * SEO metadata for question detail pages.
 */
export interface QuestionSeoData {
  jsonLd: Record<string, unknown>;
  jsonLdScript: string;
  metaTags: string;
}

// -----------------------------------------------------------------------------
// PUBLIC RFI PROJECTION (PR D — BL-QA-RFI-PUBLIC-UI-001)
// -----------------------------------------------------------------------------
// Display-safe, read-only projection of a question's "still needed" RFI state
// for public consumption (widget = PR E; MSPL/SEO = PR F-public). This is a
// DEDICATED public contract — NOT a stripped admin DTO. It exposes ONLY the
// staff request prose + a per-item checklist with human-friendly state labels,
// derived from the question's NON-TERMINAL RFIs. It NEVER carries internal ids,
// admin identity, raw enums, review timing, reply bodies, linked-response rows,
// `valueText`, attachment/media data, or any other internal RFI field
// (ADR-017 §3.7 display-safe allow-list). RFI is workflow content and never
// enters QAPage `acceptedAnswer`/`suggestedAnswer`/`answerCount` (ADR-009 §8 /
// ADR-020 §6).

/**
 * Public, display-safe per-item state (ratified label map, PR D D-B). Folds the
 * internal review-status vocabulary into three public buckets so internal review
 * timing never leaks:
 *   - `STILL_NEEDED`        ("Still needed")              ← review_status OPEN
 *   - `INFO_RECEIVED`       ("Information received")       ← UNREVIEWED | COMPLETE
 *   - `MORE_DETAIL_NEEDED`  ("More details still needed")  ← PARTIAL | INSUFFICIENT
 */
export type PublicRfiItemState = 'STILL_NEEDED' | 'INFO_RECEIVED' | 'MORE_DETAIL_NEEDED';

/**
 * Public-facing display label for each {@link PublicRfiItemState}. Single source
 * of truth for the human-readable badge/label text, shared across surfaces: the
 * storefront widget (`RfiStillNeeded.tsx`) and the worker MSPL static renderer
 * (`PublicRendererService.renderRfiBlock`) both consume these so the strings can
 * never drift between the live widget and the server-rendered page. Keyed on the
 * PUBLIC state union (never the internal review-status vocabulary). The badge
 * *colour* stays a per-surface concern — only the label text is shared here.
 */
export const PUBLIC_RFI_STATE_LABELS = {
  STILL_NEEDED: 'Still needed',
  INFO_RECEIVED: 'Information received',
  MORE_DETAIL_NEEDED: 'More details still needed',
} as const satisfies Record<PublicRfiItemState, string>;

/**
 * Public item type. A DELIBERATE separate public union that currently mirrors
 * the internal `RfiItemType` values; the worker's mapper is exhaustive /
 * fail-loud so a future schema-enum addition fails typecheck/tests rather than
 * silently leaking. Photos surface as text only (PR D D-D — no media URLs).
 */
export type PublicRfiItemType = 'photo' | 'measurement' | 'text' | 'selection' | 'confirmation';

/** A single requested item in the public "still needed" checklist. */
export interface PublicRfiItem {
  label: string;
  itemType: PublicRfiItemType;
  required: boolean;
  state: PublicRfiItemState;
}

/**
 * A single non-terminal RFI request, display-safe. `message` is the staff-
 * authored request prose returned as plain text exactly as stored (PR D D-E) —
 * consuming surfaces (PR E/F-public) own escaping/rendering. `requestedAt` is
 * epoch milliseconds (consistent with the other public timestamps). `state` is
 * the deterministic request-level rollup of `items`.
 */
export interface PublicRfiRequest {
  message: string;
  requestedAt: number;
  state: PublicRfiItemState;
  items: PublicRfiItem[];
}

/**
 * Public RFI projection for a question. `null` (on the response field) means the
 * question has no non-terminal RFI. `heading` is composed from the central
 * staff label (e.g. "OGS Staff requested more information").
 */
export interface PublicRfiProjection {
  heading: string;
  requests: PublicRfiRequest[];
}

/**
 * Response for GET /api/public/questions/:id
 */
export interface QuestionDetailResponse {
  question: PublicQuestionDetail;
  seo: QuestionSeoData;
  replies: PublicReply[];
  suggestedProducts: SuggestedProduct[];
  /**
   * Display-safe RFI "still needed" projection (PR D — BL-QA-RFI-PUBLIC-UI-001),
   * or `null` when the question has no non-terminal RFI. Embedded here to avoid a
   * second round trip; widget rendering is PR E, MSPL/JSON-LD is PR F-public.
   * Required field (always present, possibly `null`).
   */
  rfi: PublicRfiProjection | null;
}

/**
 * Request body for POST /api/public/questions
 *
 * This is the API contract type (what the client sends).
 * Images are uploaded separately via POST /api/public/upload.
 *
 * @see CreateQuestionInput for the service layer type
 */
export interface SubmitQuestionRequest {
  title: string;
  description?: string;
  posterName: string;
  posterEmail: string;
  customerId?: string;
  turnstileToken: string;
  attachmentIds?: string[];
  /** Window type categorization (P1-29) */
  windowType?: WindowType;
  /**
   * Explicit consent to public display (BL-PRIVACY-CONSENT-CAPTURE-001 v1,
   * ADR-004 §4.12.1). The submitter affirms that their question text/details
   * and any attached images may be published publicly under the merchant's
   * domain. The server REJECTS submission with a 400 ValidationError when this
   * is missing or not strictly `true`, before any question creation or
   * downstream side effects. Distinct from terms-of-service acceptance.
   */
  consentToPublicDisplay: boolean;
}

/**
 * Server-built public-display consent record (BL-PRIVACY-CONSENT-CAPTURE-001
 * v1, ADR-004 §4.12.1).
 *
 * Persisted in `questions.metadata.consent` / `replies.metadata.consent` (no
 * schema/migration). Every field is derived SERVER-SIDE — the only thing the
 * client supplies is the boolean affirmation (`consentToPublicDisplay`). The
 * record is NEVER exposed on public list/detail/MSPL/JSON-LD surfaces; `ip` is
 * admin-only PII and must never be rendered publicly or logged in plaintext.
 */
export interface PublicDisplayConsentRecord {
  /** Always `true` — a record is only written when consent was affirmed. */
  publicDisplay: true;
  /** Consent policy version (e.g. `"public-display-v1"`). */
  version: string;
  /** Stable key identifying the exact consent copy shown to the submitter. */
  textKey: string;
  /** Server timestamp the consent was recorded (ISO-8601). */
  grantedAt: string;
  /** Parsed client IP at submission, or `null` if unavailable. Admin-only PII. */
  ip: string | null;
  /** Accept-Language-derived primary locale tag, or `null` if unavailable. */
  locale: string | null;
}

/**
 * Service layer input for creating a question.
 *
 * This is distinct from SubmitQuestionRequest because:
 * - storeHash comes from middleware, not client
 * - guestToken is generated server-side
 * - Turnstile validation happens before service layer
 *
 * @see SubmitQuestionRequest for the API contract type
 */
export interface CreateQuestionInput {
  storeHash: string;
  title: string;
  description?: string;
  posterName: string;
  posterEmail: string;
  customerId?: string;
  guestToken?: string;
  attachmentIds?: string[];
  /** Window type categorization (P1-29) */
  windowType?: WindowType;
  /**
   * Server-built public-display consent record (BL-PRIVACY-CONSENT-CAPTURE-001).
   * Built by the route after the client affirms consent; persisted into
   * `questions.metadata.consent`. The route is responsible for rejecting
   * submissions that lack consent before constructing this.
   */
  consent?: PublicDisplayConsentRecord;
}

/**
 * Opaque upload-capability handle returned in submit responses (ADR-024
 * §10.3/§10.5/§11 — cross-site guest-identity transport).
 *
 * The widget holds this IN MEMORY ONLY and re-sends `token` as the
 * `X-Upload-Proof` request header on the immediately-following image `POST`(s),
 * so ownership survives on browsers that block the third-party `guest_token`
 * cookie (iOS Safari / Firefox / 3PC-restricted Chromium). `token` is an opaque
 * signed handle — it carries NO recoverable identity; the raw `guest_token` is
 * recovered server-side from strongly-consistent D1 capability state. Optional
 * so old and new widgets coexist. NEVER log, persist, or place `token` in a URL.
 */
export interface UploadCapabilityDescriptor {
  /** Opaque signed handle: `base64url(claims).base64url(HMAC(claims))`. */
  token: string;
  /** Expiry as Unix epoch seconds (informational for the client). */
  expiresAt: number;
  /** Max upload attempts this capability authorizes (replay bound, ≥ image cap). */
  maxUses: number;
  /** Bound target kind — must match the upload route. */
  targetType: 'question' | 'reply';
  /** Bound target id — must match the upload route `:id` param. */
  targetId: string;
}

/**
 * Cross-site management capability (ADR-024 §10.4 — read/self-delete leg). An
 * opaque signed handle that backs a cookieless view + self-delete of THIS
 * submission (the `/manage` magic-link + success-screen link), so ITP users
 * retain a durable self-service path without per-tenant DNS. Longer-lived than
 * the upload proof; carries no recoverable identity. Never log the token.
 */
export interface ManageCapabilityDescriptor {
  /** Opaque signed handle for the management leg. */
  token: string;
  /** Expiry as Unix epoch seconds. */
  expiresAt: number;
}

/**
 * Response for POST /api/public/questions
 *
 * Note: The guest token is set via HttpOnly cookie (Set-Cookie header),
 * NOT returned in the response body. See PHASE_0B_SPEC.md §3.1.
 */
export interface SubmitQuestionResponse {
  questionId: string;
  slug: string;
  status: string;
  message: string;
  /**
   * Cross-site upload-proof capability (ADR-024 §10.5). Present when the server
   * minted one for the immediately-following image-upload leg. Optional for
   * forward/backward compatibility between widget and worker versions.
   */
  uploadCapability?: UploadCapabilityDescriptor;
  /**
   * Cross-site management capability (ADR-024 §10.4). Present when the server
   * minted one — backs the cookieless view + self-delete of this submission.
   */
  manageCapability?: ManageCapabilityDescriptor;
}

/**
 * Public config for the storefront widget.
 */
export interface PublicConfig {
  turnstileSiteKey: string;
  maxImagesPerSubmission: number;
  maxImageSizeMb: number;
}

// =============================================================================
// PUBLIC REPLY SUBMISSION TYPES (P1-21)
// =============================================================================

/**
 * Request body for POST /api/public/questions/:questionId/replies
 *
 * ARCHITECTURE REFERENCE: Section 6.3 - Public Reply Submission
 * PHASE REFERENCE: PHASE_1_SPEC.md Task P1-21
 */
export interface SubmitReplyRequest {
  /** Reply content (min 10 chars, max 10000 chars) */
  body: string;
  /** Author display name */
  authorName: string;
  /** Author email (for notifications) */
  authorEmail: string;
  /** Turnstile verification token (required) */
  turnstileToken: string;
  /** Optional: Customer ID if logged in */
  customerId?: string;
  /**
   * Explicit consent to public display (BL-PRIVACY-CONSENT-CAPTURE-001 v1,
   * ADR-004 §4.12.1). The author affirms that their reply may be published
   * publicly under the merchant's domain. The server REJECTS the reply with a
   * 400 ValidationError when this is missing or not strictly `true`, before
   * any reply creation or downstream side effects. Distinct from ToS acceptance.
   */
  consentToPublicDisplay: boolean;
}

/**
 * Service layer input for creating a reply.
 *
 * This is distinct from SubmitReplyRequest because:
 * - storeHash comes from middleware, not client
 * - questionId comes from route param
 * - guestToken is read from cookie or generated
 * - Turnstile validation happens before service layer
 */
export interface CreateReplyInput {
  storeHash: string;
  questionId: string;
  body: string;
  authorName: string;
  authorEmail: string;
  customerId?: string;
  guestToken?: string;
  /**
   * Server-built public-display consent record (BL-PRIVACY-CONSENT-CAPTURE-001).
   * Built by the route after the author affirms consent; persisted into
   * `replies.metadata.consent`.
   */
  consent?: PublicDisplayConsentRecord;
}

/**
 * Response for POST /api/public/questions/:questionId/replies
 */
export interface SubmitReplyResponse {
  replyId: string;
  status: string;
  message: string;
  /**
   * Cross-site upload-proof capability (ADR-024 §10.5). Present when the server
   * minted one for the immediately-following reply-image-upload leg. The widget
   * reply form consumes this when the customer attaches photos to a reply
   * (ADR-022 §12 PR5); optional for widget/worker version coexistence and for
   * tenants where reply images are disabled.
   */
  uploadCapability?: UploadCapabilityDescriptor;
}

// =============================================================================
// ADMIN API TYPES
// =============================================================================

/**
 * Quality score for a question (P1-28).
 * Helps admins prioritize high-quality questions and flag low-quality ones.
 */
export interface QualityScore {
  /** Total score (0-40) */
  score: number;
  /** Quality level derived from score */
  level: 'LOW' | 'MEDIUM' | 'HIGH';
  /** Individual scoring factors */
  factors: {
    /** Points from title length (0-10) */
    titleLength: number;
    /** Points from description length (0-15) */
    descriptionLength: number;
    /** Points from having images (0-10) */
    hasImages: number;
    /** Points from grammar/punctuation (0-5) */
    grammar: number;
  };
}

/**
 * Admin question list item (includes email, images, and sensitive data).
 *
 * Note: This is intentionally NOT extending PublicQuestionListItem because
 * the admin list response includes different fields (e.g., updatedAt, imageCount)
 * and omits some public fields (e.g., hasSolution, thumbnailUrl).
 */
export interface AdminQuestionListItem {
  questionId: string;
  title: string;
  slug: string;
  description: string | null;
  status: string;
  posterName: string;
  posterEmail: string;
  /** ISO 8601 string (Date serialized via JSON) */
  createdAt: string;
  /** ISO 8601 string (Date serialized via JSON) */
  updatedAt: string;
  replyCount: number;
  /**
   * Count of replies on this question awaiting moderation
   * (moderationStatus = 'PENDING_REVIEW'). Tenant-scoped.
   * Surfaces pending-reply workload in the moderation queue
   * (BL-ADMIN-MODERATION-QUEUE-VISIBILITY-001).
   */
  pendingReplyCount: number;
  imageCount: number;
  /**
   * Count of images on this question awaiting moderation
   * (media moderationStatus = 'PENDING_REVIEW'). Tenant-scoped, batched
   * (never N+1). ADR-022 §10(C) per-row pending badge —
   * BL-IMG-ADMIN-MODERATION-001.
   */
  pendingImageCount: number;
  images: ImageAttachment[];
  /** Quality score for admin prioritization (P1-28) */
  qualityScore: QualityScore;
}

/**
 * Admin question detail (full data for single question view).
 * Response shape for GET /api/admin/questions/:questionId
 *
 * GAP-004: Includes tags and suggestedProductIds for admin editing.
 */
export interface AdminQuestionDetail {
  questionId: string;
  title: string;
  slug: string;
  description: string | null;
  status: string;
  posterName: string;
  posterEmail: string;
  /** ISO 8601 string (Date serialized via JSON) */
  createdAt: string;
  /** ISO 8601 string (Date serialized via JSON) */
  updatedAt: string;
  tags: string[];
  suggestedProductIds: number[];
  imageCount: number;
  images: ImageAttachment[];
  qualityScore: QualityScore;
}

/**
 * Response for GET /api/admin/questions/:questionId
 */
export interface AdminQuestionDetailResponse {
  question: AdminQuestionDetail;
}

/**
 * Query parameters for GET /api/admin/questions
 */
export interface AdminQuestionFilters extends PaginationParams {
  status?: 'PENDING_REVIEW' | 'OPEN' | 'IN_PROGRESS' | 'NEEDS_MORE_INFO' | 'RESOLVED' | 'CLOSED_DUPLICATE' | 'CLOSED_REJECTED';
  search?: string;
  /** Filter to only unanswered questions (reply_count = 0) */
  unanswered?: boolean;
  /** Filter to only questions with image attachments */
  hasImages?: boolean;
  /**
   * Filter to only questions that have at least one reply awaiting
   * moderation (moderationStatus = 'PENDING_REVIEW').
   * BL-ADMIN-MODERATION-QUEUE-VISIBILITY-001.
   */
  needsReview?: boolean;
  /**
   * Filter to only questions that have at least one image awaiting
   * moderation (media moderationStatus = 'PENDING_REVIEW').
   * ADR-022 §10(C) "Pending images" filter — BL-IMG-ADMIN-MODERATION-001.
   */
  pendingImages?: boolean;
  /** Sort field. Default: 'createdAt'. P1-10: Added 'status' option. */
  sortBy?: 'createdAt' | 'updatedAt' | 'status';
  /** Sort direction. Default: 'desc' */
  sortOrder?: 'asc' | 'desc';
}

/**
 * Summary statistics for admin dashboard.
 */
export interface QuestionStats {
  pendingCount: number;
  openCount: number;
  resolvedCount: number;
  /**
   * Store-wide count of replies awaiting moderation
   * (moderationStatus = 'PENDING_REVIEW') across all questions.
   * Surfaces pending-reply workload in the moderation queue
   * (BL-ADMIN-MODERATION-QUEUE-VISIBILITY-001).
   */
  pendingRepliesCount: number;
}

/**
 * Response for GET /api/admin/questions
 */
export interface AdminQuestionListResponse {
  questions: AdminQuestionListItem[];
  stats: QuestionStats;
  pagination: PaginationMeta;
}

/**
 * Request body for PATCH /api/admin/questions/:id
 */
export interface QuestionStatusExpectedState {
  status: QuestionStatus;
}

/** Load-time status baseline for marking a reply as the solution (ADR-027 Slice 4). */
export interface QuestionSolutionExpectedState {
  status: QuestionStatus;
}

/**
 * Optional body for PATCH …/replies/:replyId/solution.
 *
 * Older clients omit the body during the ADR-027 expand/contract window. A
 * supplied token is validated strictly; solution unmark remains bodyless and
 * tokenless by design.
 */
export interface MarkSolutionRequest {
  expectedState?: QuestionSolutionExpectedState;
}

/**
 * Load-time baselines for the compound question PATCH (ADR-027 D-3, slice 3).
 * When present, its keys MUST exactly match the request's updatable fields —
 * a request updating status+tags carries `{ status, tags }`, a tags-only
 * request carries `{ tags }`, and so on (all-or-nothing per included sub-op;
 * subset/superset keysets are rejected as malformed). Array baselines are
 * server-issued at read time and echoed opaquely; clients must never
 * reconstruct or reorder them.
 */
export interface QuestionPatchExpectedState {
  status?: QuestionStatus;
  tags?: string[];
  suggestedProductIds?: number[];
}

export type QuestionPatchSubOperation = 'status' | 'tags' | 'products';
export type QuestionPatchSubOperationOutcome = 'committed' | 'conflicted' | 'not_attempted';

/**
 * Per-operation result carried on a compound question PATCH failure. A later
 * slice narrows the tag/product observations; Slice 2 uses the status member.
 */
export interface QuestionPatchSubOperationResult {
  op: QuestionPatchSubOperation;
  outcome: QuestionPatchSubOperationOutcome;
  observed?: QuestionStatus | string[] | number[];
}

/** Display-safe status conflict returned by ADR-027 guarded question writes. */
export interface QuestionStatusConflict {
  surface: 'question_status';
  contested: ['status'];
  expected: QuestionStatusExpectedState;
  observed: QuestionStatusExpectedState;
  recovery: 'direct';
}

/** Display-safe status conflict returned by solution mark/unmark (ADR-027 Slice 4). */
export interface QuestionSolutionConflict {
  surface: 'question_solution';
  contested: ['status'];
  expected: QuestionSolutionExpectedState;
  observed: QuestionSolutionExpectedState;
  recovery: 'direct';
}

/**
 * Bounded array observation carried in a tags/products conflict (ADR-027
 * slice 3). A conformant stored value is carried as the validated array; a
 * non-conformant raw value is marker-encoded (`SETTINGS_CONFLICT_MALFORMED_VALUE`
 * discipline) and forces `recovery: 'refresh_required'` — the marker is display
 * evidence only and must never be echoed back as a CAS baseline.
 */
export type QuestionTagsObservation = string[] | 'malformed_raw_value';
export type QuestionProductsObservation = number[] | 'malformed_raw_value';

/** Display-safe tags conflict returned by the guarded tags writer (slice 3). */
export interface QuestionTagsConflict {
  surface: 'question_tags';
  contested: ['tags'];
  expected: { tags: QuestionTagsObservation };
  observed: { tags: QuestionTagsObservation };
  recovery: 'direct' | 'refresh_required';
}

/** Display-safe products conflict returned by the guarded products writer (slice 3). */
export interface QuestionProductsConflict {
  surface: 'question_products';
  contested: ['suggestedProductIds'];
  expected: { suggestedProductIds: QuestionProductsObservation };
  observed: { suggestedProductIds: QuestionProductsObservation };
  recovery: 'direct' | 'refresh_required';
}

export interface UpdateQuestionRequest {
  status?: QuestionStatus;
  tags?: string[];
  /** BigCommerce product entity IDs to link as suggested parts (max 10) */
  suggestedProductIds?: number[];
  /**
   * Load-time baselines for the requested sub-operations. Older clients may
   * omit it during the ADR-027 expand/contract window; a supplied value is
   * strictly validated and its keys must exactly match the request's
   * updatable fields (ADR-027 D-3, slice 3).
   */
  expectedState?: QuestionPatchExpectedState;
  /**
   * ADR-025 A-Q1 (OD-19): REQUIRED `QUESTION_REJECTION_REASONS` member when
   * `status = "CLOSED_REJECTED"`; FORBIDDEN (400) on any other request.
   */
  rejectionReason?: string;
  /**
   * Optional internal note (trimmed; ≤ QUESTION_REJECTION_NOTE_MAX_LENGTH,
   * else 400). Stored in the audit row's metadata; NEVER rendered by the
   * ADR-023 display-safe projection. Only valid alongside `rejectionReason`.
   */
  rejectionNote?: string;
}

/**
 * Response for PATCH /api/admin/questions/:id
 */
export interface UpdateQuestionResponse {
  questionId: string;
  status: QuestionStatus;
  tags: string[];
  suggestedProductIds: number[];
  /** ISO 8601 string (Date serialized via JSON) */
  updatedAt: string;
  message: string;
}

// -----------------------------------------------------------------------------
// ADMIN AUDIT-TRAIL VIEWER (PR I-1 — BL-ADMIN-AUDIT-VIEWER-001, ADR-023)
// -----------------------------------------------------------------------------
// Display-safe, read-only projection of `audit_logs` rows for the admin-only
// audit-trail viewer (`GET /api/admin/audit-logs`, `AUDIT_VIEW`-gated). The
// projection is fail-closed: raw `previousValue`/`newValue`/`metadata` blobs are
// NEVER serialized wholesale — only allow-listed, scalar/enum/count-derived
// fields per action reach `details`. The staff `actor` is the admin email (an
// owner-ratified, same-tenant accountability disclosure — ADR-023); customer /
// participant email + email-hash are never exposed. See ADR-023 + the
// AuditLogViewProjection module for the full per-action redaction contract.

/**
 * Display-safe view of a single audited entity reference.
 *
 * `id` is OMITTED for entity types whose id is sensitive or non-actionable in
 * this surface: `PARTICIPANT` (email hash) and `STORE_SETTINGS` (= store hash).
 */
export interface AuditLogEntityView {
  type: AuditEntityType;
  /** Internal entity id (e.g. `q_…`, `r_…`, `rfi_…`); omitted for PARTICIPANT / STORE_SETTINGS. */
  id?: string;
  /** Human-readable entity label, e.g. "Question", "RFI request". */
  label: string;
}

/**
 * Display-safe projection of an `audit_logs` row for the admin audit viewer.
 *
 * Produced by `toAuditLogView(entry)` (worker). Contains ONLY non-sensitive,
 * allow-listed fields — never raw before/after blobs, customer/participant
 * email or email-hash, tag text, reply/question bodies, or free-text reasons.
 */
export interface AuditLogView {
  logId: string;
  action: AuditAction;
  /**
   * Optional bounded, outcome-aware label for the Action column. The raw
   * `action` remains the stable filter/audit enum; this label prevents a
   * rejected guarded attempt from being presented as a completed action.
   */
  displayAction?: string;
  entity: AuditLogEntityView;
  /** Staff actor label (admin email under `AUDIT_VIEW`; see ADR-023). */
  actor: string;
  /** ISO 8601 string (`createdAt.toISOString()`). */
  createdAt: string;
  /** Per-action allow-listed detail rows (scalar / enum / count / internal-id only). */
  details: Array<{ label: string; value: string }>;
  /** True when a free-text reason exists on the row; the reason text is NEVER included. */
  reasonRecorded?: boolean;
}

/**
 * Response body for `GET /api/admin/audit-logs`.
 *
 * Keyset (cursor) paginated — NO offset/page/total. `nextCursor` is a
 * transparent, format-validated cursor token (`${epochSeconds}.${logId}`) — it
 * is shape/length/range-checked on the way in, not cryptographically signed (a
 * forged value can only reshuffle the caller's own tenant-scoped rows). It is
 * non-null only when a further page exists. Feed it back as `cursor` to load
 * the next (older) page; `null` means the end of the trail.
 */
export interface AuditLogListResponse {
  logs: AuditLogView[];
  nextCursor: string | null;
}

/**
 * Query parameters for `GET /api/admin/audit-logs`.
 *
 * Cursor-paginated (no `page`/`offset`). `fromDate`/`toDate` are full ISO-8601
 * datetimes (inclusive). There is deliberately NO actor/`adminEmail` filter —
 * staff identity is never placed in a query string (see ADR-023); client-side
 * actor filtering of loaded rows lives in the I-2 UI.
 */
export interface AuditLogListQuery {
  action?: AuditAction;
  entityType?: AuditEntityType;
  entityId?: string;
  /** Inclusive lower bound — full ISO-8601 datetime. */
  fromDate?: string;
  /** Inclusive upper bound — full ISO-8601 datetime. */
  toDate?: string;
  /** Page size (default 20, min 1, max 100). */
  limit?: number;
  /** Opaque-to-callers cursor token from a previous `nextCursor`. */
  cursor?: string;
}

/**
 * Request body for POST /api/admin/support/participant-lookup
 * (BL-ADMIN-PARTICIPANT-SUPPORT-LOOKUP-001).
 *
 * Read-only, audited, exact-email participant support lookup. POST (not GET) so
 * the email is never placed in a query string / URL / access log. Exact-match
 * only — there is NO browse/list/enumeration surface.
 */
export interface ParticipantSupportLookupRequest {
  /** Exact participant email to resolve (validated server-side). */
  email: string;
}

/**
 * Response for POST /api/admin/support/participant-lookup.
 *
 * Returns ONLY the participant's notification-subscription state. The raw email
 * is never echoed, and the privacy-preserving email hash is deliberately NOT
 * returned to the client (it is recorded server-side as the audit entity id, but
 * the Admin UI has no need for it). It also carries NO linked questions/replies/
 * queue rows and no plaintext participant list. When `hasPreferenceRecord` is
 * false, the boolean flags reflect the system DEFAULT subscription state that the
 * send path would apply.
 */
export interface ParticipantSupportLookupResponse {
  /** Whether a stored preference row exists (false ⇒ flags are effective defaults). */
  hasPreferenceRecord: boolean;
  /** Global opt-out — when true, all notifications are suppressed. */
  globalUnsubscribe: boolean;
  replyNotifications: boolean;
  questionApproved: boolean;
  questionResolved: boolean;
  /** ISO 8601 string, or null when no stored preference row exists. */
  createdAt: string | null;
  /** ISO 8601 string, or null when no stored preference row exists. */
  updatedAt: string | null;
}

/**
 * Request body for POST /api/admin/support/participant-resubscribe
 * (BL-ADMIN-NOTIFICATION-RESUBSCRIBE-001).
 *
 * Audited, consent-gated re-enable of a globally-unsubscribed participant's
 * notifications. v1 is an audited ADMIN re-enable with NO email sent — recipient
 * double-opt-in confirmation is deliberately deferred. POST (not GET) so the
 * email is never placed in a query string / URL / access log. Exact-match only —
 * there is NO browse/list/enumeration surface.
 *
 * Compliance gate (CAN-SPAM / CASL / Quebec Law 25): the operator must supply a
 * non-empty `reason` documenting the participant's request AND explicitly affirm
 * `userRequestConfirmed: true`. The server rejects the write otherwise.
 */
export interface ParticipantResubscribeRequest {
  /** Exact participant email to re-enable (validated server-side). */
  email: string;
  /** Operator-authored, non-empty justification recorded in the audit log. */
  reason: string;
  /**
   * Explicit affirmation that the operator has a documented request from this
   * participant to be re-subscribed. MUST be literally `true`.
   */
  userRequestConfirmed: true;
}

/**
 * Response for POST /api/admin/support/participant-resubscribe.
 *
 * Returns ONLY the participant's resulting notification-subscription state
 * (mirrors {@link ParticipantSupportLookupResponse}). After a successful
 * re-enable `globalUnsubscribe` is `false`; the per-type flags are preserved as
 * they were. The raw email is never echoed, the privacy-preserving email hash is
 * deliberately NOT returned to the client, and there are NO linked
 * questions/replies/queue rows.
 */
export interface ParticipantResubscribeResponse {
  /** Whether a stored preference row exists after the write (always true here). */
  hasPreferenceRecord: boolean;
  /** Global opt-out — `false` after a successful re-enable. */
  globalUnsubscribe: boolean;
  replyNotifications: boolean;
  questionApproved: boolean;
  questionResolved: boolean;
  /** ISO 8601 string, or null when no stored preference row exists. */
  createdAt: string | null;
  /** ISO 8601 string, or null when no stored preference row exists. */
  updatedAt: string | null;
}

/**
 * Request body for POST /api/admin/support/consent-inspection
 * (BL-PRIVACY-CONSENT-CAPTURE-001 — admin consent-inspection surface).
 *
 * Read-only, audited inspection of the already-captured public-display consent
 * records for an EXPLICIT question scope (the question + its replies), so an
 * operator can answer "what did this person consent to, when, and under which
 * policy version?" for support / DSAR purposes without raw D1 access.
 *
 * The operator names the scope by exact `questionId` — there is NO
 * browse/list/search/enumeration surface and NO email-scoped lookup in v1.
 */
export interface ConsentInspectionRequest {
  /** Exact question id whose consent footprint is being inspected. */
  questionId: string;
}

/**
 * A single public-display consent record projection, scoped to one entity
 * (the question itself or one of its replies).
 *
 * Derived from the stored {@link PublicDisplayConsentRecord} in the entity's
 * `metadata.consent`. The admin-only raw `ip` is DELIBERATELY NOT included in
 * v1 (privacy posture): only its presence is surfaced via {@link ipAvailable}.
 * Surfacing the raw IP would be a separate, consciously-reviewed decision.
 */
export interface ConsentInspectionRecord {
  /** Which entity this record belongs to. */
  entityType: 'QUESTION' | 'REPLY';
  /** The question id, or the reply id, the record was read from. */
  entityId: string;
  /** Whether a `metadata.consent` record exists for this entity at all. */
  hasConsentRecord: boolean;
  /**
   * Whether the stored record affirms public display (`publicDisplay === true`).
   * `false` when no record exists or the stored value is malformed/absent.
   */
  consentPresent: boolean;
  /** Mirror of the stored `publicDisplay` flag, or `null` when no record exists. */
  publicDisplay: boolean | null;
  /** Consent policy version, or `null` when no record exists / malformed. */
  version: string | null;
  /** Stable consent-copy key, or `null` when no record exists / malformed. */
  textKey: string | null;
  /** ISO-8601 server timestamp the consent was recorded, or `null`. */
  grantedAt: string | null;
  /** Accept-Language-derived primary locale tag, or `null`. */
  locale: string | null;
  /**
   * Whether an admin-only client IP was captured with this consent record. The
   * raw IP itself is NOT returned in v1 — only its availability.
   */
  ipAvailable: boolean;
}

/**
 * Response for POST /api/admin/support/consent-inspection.
 *
 * Returns ONLY the consent-record projections for the scoped question and its
 * replies. Carries NO raw email, NO email hash, NO question/reply free text, NO
 * raw IP, and NO full `metadata` blob — only the bounded consent fields in
 * {@link ConsentInspectionRecord}. `questionFound` is `false` when the scoped
 * question does not exist for the authenticated tenant (in which case `records`
 * is empty).
 */
export interface ConsentInspectionResponse {
  /** Echo of the inspected question id. */
  questionId: string;
  /** Whether the scoped question exists for the authenticated tenant. */
  questionFound: boolean;
  /**
   * Consent-record projections — the question record first (when the question
   * exists), followed by one record per reply under that question. Replies with
   * no stored consent still appear with `hasConsentRecord: false` so the
   * operator sees the full footprint.
   */
  records: ConsentInspectionRecord[];
}

/**
 * Request body for POST /api/admin/questions/:questionId/replies
 * (staff-authored reply — track phase 1, `BL-STAFF-AUTHORING-001`).
 *
 * Deliberately NARROW: the ONLY client-accepted field is the plain-text body.
 * Authority-bearing fields are SERVER-DERIVED and must NEVER be accepted from
 * the client, preserving the immutable-authorship trust boundary
 * (ADR-017 §3.1/§3.4):
 *   - `userType` ('ADMIN'), `userId`, `authorName`, `authorEmail` — from the
 *     verified admin session, not the request.
 *   - `replyType` ('GENERAL'), `isSolution` (false), `moderationStatus`
 *     ('APPROVED'), `recommendedProductIds` — fixed v1 server policy.
 *   - `storeHash` — from `adminAuthMiddleware` context.
 *
 * (Supersedes the orphaned Phase-2-era `AdminReplyRequest`, which was never
 * wired to a route and carried client-asserted authority fields.)
 */
export interface CreateStaffReplyRequest {
  /** Reply content (min 10 chars, max 10000 chars). The only accepted field. */
  body: string;
}

/**
 * Response payload for POST /api/admin/questions/:questionId/replies.
 *
 * Display-safe: NEVER includes `authorEmail`, `userId`, raw actor identity, or
 * capability internals.
 *
 * As with the sibling `ModerateReplyResponse`, the route may additionally append
 * an `mspl` republish-outcome diagnostic. That outcome is a worker-only shape
 * (`NotifyMSPLQuestionChangedResult`) and is intentionally NOT part of this
 * shared contract — shared API types must not depend on worker-internal types.
 */
export interface CreateStaffReplyResponse {
  reply: {
    replyId: string;
    questionId: string;
    moderationStatus: ReplyModerationStatus;
    createdAt: string;
  };
  message: string;
}

// =============================================================================
// ADMIN RFI (REQUEST-FOR-INFORMATION) TYPES — PR B (BL-QA-RFI-TABLES-001)
// =============================================================================
// Admin-facing DTOs for the official RFI request foundation (ADR-020 §5 / §5.2).
// These mirror the worker Drizzle `RfiStatus` / `RfiItemType` /
// `RfiItemReviewStatus` unions for cross-package (admin-ui) consumption. There
// is intentionally NO public RFI DTO here — public RFI-aware projection is
// deferred to PR D/E.

/**
 * Lifecycle status of an official RFI request. The rollup auto-produces
 * `OPEN` / `PARTIALLY_ANSWERED` / `ANSWERED` (PR C); `CANCELLED` is produced by
 * the staff cancel/withdraw transition (PR H — never auto-produced). `SUPERSEDED`
 * (replace-by-follow-up) is the remaining terminal transition, still deferred.
 */
export type RfiStatus =
  | 'OPEN'
  | 'PARTIALLY_ANSWERED'
  | 'ANSWERED'
  | 'SUPERSEDED'
  | 'CANCELLED';

/** Kind of information an RFI item requests. */
export type RfiItemType = 'photo' | 'measurement' | 'text' | 'selection' | 'confirmation';

/** Per-item review state. PR B only ever emits `OPEN`. */
export type RfiItemReviewStatus =
  | 'OPEN'
  | 'UNREVIEWED'
  | 'INSUFFICIENT'
  | 'PARTIAL'
  | 'COMPLETE';

/**
 * A single requested item within an RFI create request.
 *
 * Allow-listed fields ONLY. Item array order is authoritative for display
 * ordering — `ordinal` is NOT accepted as input (an item carrying `ordinal`
 * is rejected by the route's unknown-field check). Authority/identity/state
 * fields (review status, satisfiedAt, ids, store/admin identity) are
 * server-derived and rejected if present in the request body.
 */
export interface CreateRfiItemInput {
  itemType: RfiItemType;
  label: string;
  required: boolean;
  /** Measurement items only. */
  unit?: string;
  /** Selection items only. */
  options?: string[];
}

/**
 * Request body for POST /api/admin/questions/:questionId/rfi.
 *
 * The ONLY accepted top-level fields are `message` and `items`. The path owns
 * `questionId`; the auth context owns store + admin identity. Any
 * authority/identity field in the body is rejected with a ValidationError.
 */
export interface CreateRfiRequest {
  message: string;
  items: CreateRfiItemInput[];
}

/**
 * Admin DTO for a single RFI response link (PR C —
 * `BL-QA-RFI-OVERLAY-LINKAGE-001`). Correlates a linked reply to an `rfi_item`.
 *
 * Admin-only. Intentionally EXCLUDES the reply body, author name, and poster
 * email — the UI correlates by `replyId` against the reply it already holds.
 */
export interface AdminRfiResponseLink {
  rfiResponseId: string;
  rfiItemId: string;
  replyId: string;
  /** Admin user id that created the link (never an email). */
  linkedBy: string;
  /** Optional representative attachment for a photo item. */
  attachmentId: string | null;
  /** Optional staff-recorded value (text/measurement/selection answer). */
  valueText: string | null;
  createdAt: string;
}

/** Admin DTO for a single RFI item (read + create responses). */
export interface AdminRfiItem {
  rfiItemId: string;
  ordinal: number;
  itemType: RfiItemType;
  label: string;
  unit: string | null;
  options: string[] | null;
  required: boolean;
  reviewStatus: RfiItemReviewStatus;
  satisfiedAt: string | null;
  /**
   * Linked responses for this item (PR C). Empty when the item is `OPEN`
   * (the evidence invariant: a non-`OPEN` item has ≥1 linked response).
   */
  responses: AdminRfiResponseLink[];
}

/**
 * Admin DTO for an RFI request (with its ordered items). May surface internal
 * actor/id fields — admin-only; never reused on public surfaces.
 */
export interface AdminRfiRequest {
  rfiId: string;
  questionId: string;
  replyId: string;
  status: RfiStatus;
  requestedBy: string;
  message: string;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  items: AdminRfiItem[];
}

/**
 * Open-side RFI status-transition outcome (PR B2 — `BL-QA-RFI-AUTOSTATUS-001`).
 *
 * Deterministic shape surfaced on a successful RFI open. After creating the RFI,
 * the route may transition an `OPEN`/`IN_PROGRESS` question to `NEEDS_MORE_INFO`
 * when the RFI carries ≥1 required item. `attempted` is true iff the status write
 * was called; `applied` is true only when it succeeded.
 *
 * `mspl` is populated ONLY when `reason === 'APPLIED'` (the structured MSPL
 * publish outcome) and is null/omitted otherwise (including `FAILED`). Typed here
 * as a minimal `{ phase }` shape for cross-package (admin-ui) consumption; the
 * worker carries the richer `NotifyMSPLQuestionChangedResult` at runtime.
 */
export interface RfiOpenStatusTransition {
  attempted: boolean;
  applied: boolean;
  /** Whether the attempted status outcome was durably audited. */
  audited?: boolean;
  /** Freshly re-read pre-transition status; null only if the question could not be re-read. */
  from: QuestionStatus | null;
  /** The attempted target; null when no transition is attempted. */
  to: 'NEEDS_MORE_INFO' | null;
  reason:
    | 'NO_REQUIRED_ITEMS'
    | 'ALREADY_NEEDS_MORE_INFO'
    | 'APPLIED'
    | 'stale_lost'
    | 'FAILED'
    | 'SKIPPED_UNEXPECTED_STATUS';
  mspl?: { phase: string } | null;
}

/** Response payload for POST /api/admin/questions/:questionId/rfi. */
export interface CreateRfiResponse {
  rfi: AdminRfiRequest;
  message: string;
  /** Open-side NEEDS_MORE_INFO transition outcome (PR B2). */
  statusTransition: RfiOpenStatusTransition;
}

/** Response payload for GET /api/admin/questions/:questionId/rfi. */
export interface AdminRfiListResponse {
  rfis: AdminRfiRequest[];
}

/**
 * Completion-side RFI status-transition outcome (PR C2 —
 * `BL-QA-RFI-AUTOSTATUS-001`). The completion-side counterpart to
 * {@link RfiOpenStatusTransition}.
 *
 * Deterministic shape surfaced on EVERY successful item-review response (incl.
 * the no-op reasons). After an item review commits, the route may CLEAR a
 * `NEEDS_MORE_INFO` question back to `IN_PROGRESS` when the review leaves the
 * question no longer RFI-blocked (≥1 gating RFI exists and all gating RFIs are
 * `ANSWERED`). This is CLEAR-ONLY: it never re-blocks. `attempted` is true iff
 * the status write was called; `applied` is true only when it succeeded.
 *
 * `mspl` is populated ONLY when `reason === 'CLEARED'` (the structured MSPL
 * publish outcome) and is null/omitted otherwise (including `FAILED`). Typed
 * here as a minimal `{ phase }` shape for cross-package (admin-ui) consumption;
 * the worker carries the richer `NotifyMSPLQuestionChangedResult` at runtime.
 */
export interface RfiCompletionStatusTransition {
  attempted: boolean;
  applied: boolean;
  /** Whether the attempted status outcome was durably audited. */
  audited?: boolean;
  /**
   * Freshly re-read pre-transition status. `NEEDS_MORE_INFO` for the clear/
   * still-blocked/no-gating reasons and for `FAILED`; the current status for
   * `NOT_NEEDS_MORE_INFO`; null only when the post-review status re-read itself
   * could not resolve the question (a `SKIPPED_UNEXPECTED_STATUS` sub-case).
   */
  from: QuestionStatus | null;
  /** The attempted clear target; null when no transition is attempted. */
  to: 'IN_PROGRESS' | null;
  reason:
    | 'CLEARED'
    | 'STILL_BLOCKED'
    | 'NO_GATING_RFI'
    | 'NOT_NEEDS_MORE_INFO'
    | 'SKIPPED_UNEXPECTED_STATUS'
    | 'stale_lost'
    | 'FAILED';
  mspl?: { phase: string } | null;
}

/**
 * Response payload for PATCH
 * /api/admin/questions/:questionId/rfi/:rfiId/items/:rfiItemId/review (PR C2).
 *
 * `statusTransition` is present on every successful review response (including
 * the no-op reasons). The link/unlink routes remain status-neutral and their
 * responses carry no `statusTransition`.
 */
export interface ReviewRfiItemResponse {
  rfi: AdminRfiRequest;
  statusTransition: RfiCompletionStatusTransition;
}

// -----------------------------------------------------------------------------
// RFI cancel / withdraw (PR H — BL-QA-RFI-CANCEL-001). Behind `RFI_MANAGE`;
// admin-facing only. Cancelling an RFI is a terminal STATE TRANSITION (not a
// deletion): the row persists as `CANCELLED` for the read-side guards (409
// re-cancel, public-projection exclusion, block-predicate exclusion) + audit
// history. No DB migration (`CANCELLED` already in `RfiStatus`); no email.
// -----------------------------------------------------------------------------

/**
 * Structured, privacy-safe cancel reasons (PR H — D-7). A *named* reason avoids
 * the PII/retention risk of a durable free-text reason in the audit log (staff
 * could paste poster names/emails/notes — against the PII-safe-audit convention).
 * The runtime list is the SINGLE SOURCE OF TRUTH — `RfiCancelReasonCode` is
 * derived from it so the union can never drift from the validated values (the
 * route AND the service validate membership at runtime; the TS union alone is
 * not trusted).
 */
export const RFI_CANCEL_REASON_CODES = [
  'DUPLICATE',
  'MISTAKE',
  'NO_LONGER_NEEDED',
  'OTHER',
] as const;
export type RfiCancelReasonCode = (typeof RFI_CANCEL_REASON_CODES)[number];

/**
 * Request body for POST /api/admin/questions/:questionId/rfi/:rfiId/cancel.
 *
 * `reasonCode` is OPTIONAL — the cancel route accepts NO body, `{}`, or
 * `{ reasonCode }`. The ONLY accepted top-level field is `reasonCode`; the path
 * owns `questionId`/`rfiId` and the auth context owns store + admin identity, so
 * any authority/identity field (or unknown key) is rejected with a
 * ValidationError. The v1 admin UI sends no body.
 */
export interface CancelRfiRequest {
  reasonCode?: RfiCancelReasonCode;
}

/**
 * Cancel-side RFI status-transition outcome (PR H — `BL-QA-RFI-CANCEL-001`).
 *
 * The cancel-side counterpart to {@link RfiCompletionStatusTransition}, with a
 * DEDICATED reason set — cancel semantics INVERT the review reasons (the info was
 * not "received"; the request was "withdrawn"), so reusing the completion type
 * would mis-map the admin-UI copy ("information received"). Deterministic shape
 * surfaced on EVERY successful cancel response (incl. the no-op reasons). After
 * the RFI is cancelled, the route may CLEAR a `NEEDS_MORE_INFO` question back to
 * `IN_PROGRESS` when the cancelled RFI was gating and the question is no longer
 * RFI-blocked — crucially INCLUDING the case where cancelling the LAST gating RFI
 * leaves no gating RFI at all (the review path's `hasGatingRfi` guard would
 * wrongly refuse to clear here, leaving the question stuck). `attempted` is true
 * iff the status write was called; `applied` is true only when it succeeded.
 *
 * `mspl` is populated ONLY when `reason === 'CLEARED_AFTER_CANCEL'` and is
 * null/omitted otherwise (including `FAILED`). Typed here as a minimal `{ phase }`
 * shape for cross-package (admin-ui) consumption; the worker carries the richer
 * `NotifyMSPLQuestionChangedResult` at runtime.
 */
export interface RfiCancelStatusTransition {
  attempted: boolean;
  applied: boolean;
  /** Whether the attempted status outcome was durably audited. */
  audited?: boolean;
  /**
   * Freshly re-read pre-transition status. `NEEDS_MORE_INFO` for the clear/
   * still-blocked/non-gating reasons and for `FAILED`; the current status for
   * `NOT_NEEDS_MORE_INFO`; null only when the post-cancel status re-read itself
   * could not resolve the question (a `SKIPPED_UNEXPECTED_STATUS` sub-case).
   */
  from: QuestionStatus | null;
  /** The attempted clear target; null when no transition is attempted. */
  to: 'IN_PROGRESS' | null;
  reason:
    | 'CLEARED_AFTER_CANCEL'
    | 'STILL_BLOCKED'
    | 'CANCELLED_NON_GATING_RFI'
    | 'NOT_NEEDS_MORE_INFO'
    | 'SKIPPED_UNEXPECTED_STATUS'
    | 'stale_lost'
    | 'FAILED';
  mspl?: { phase: string } | null;
}

/** Response payload for POST /api/admin/questions/:questionId/rfi/:rfiId/cancel. */
export interface CancelRfiResponse {
  rfi: AdminRfiRequest;
  statusTransition: RfiCancelStatusTransition;
}

// -----------------------------------------------------------------------------
// RFI response linking + item review payloads (PR C —
// BL-QA-RFI-OVERLAY-LINKAGE-001). Behind `RFI_MANAGE`; admin-facing only.
// -----------------------------------------------------------------------------

/**
 * A single reply→item link within a {@link CreateRfiResponseLinkInput}. The
 * reply being linked is owned by the request body's top-level `replyId`; each
 * entry targets one `rfiItemId` and may carry an optional representative
 * `attachmentId` (photo items) and/or a `valueText`.
 */
export interface RfiResponseLinkTarget {
  rfiItemId: string;
  /** Optional representative attachment (photo items only). */
  attachmentId?: string;
  /** Optional staff-recorded value (trimmed, ≤ MAX_RFI_RESPONSE_VALUE_TEXT_LENGTH). */
  valueText?: string;
}

/**
 * Request body for POST /api/admin/questions/:questionId/rfi/:rfiId/responses.
 *
 * Links one APPROVED, non-deleted, `GENERAL` reply (`replyId`) on the same
 * question to one or more `rfi_items` (`links`, 1..MAX_RFI_ITEMS, no duplicate
 * `rfiItemId`s). The path owns `questionId`/`rfiId`; the auth context owns store
 * + admin identity. Authority/identity fields in the body are rejected.
 */
export interface CreateRfiResponseLinkInput {
  replyId: string;
  links: RfiResponseLinkTarget[];
}

/**
 * Request body for PATCH
 * /api/admin/questions/:questionId/rfi/:rfiId/items/:rfiItemId/review.
 *
 * `reviewStatus` ∈ {UNREVIEWED, INSUFFICIENT, PARTIAL, COMPLETE} (staff-zone
 * states; each requires ≥1 linked response). `OPEN` is rejected — it is
 * system-derived. `UNREVIEWED` is the explicit "clear my assessment" reset.
 */
export interface ReviewRfiItemInput {
  reviewStatus: Exclude<RfiItemReviewStatus, 'OPEN'>;
}

// =============================================================================
// ADMIN REPLY MODERATION TYPES (GAP-002)
// =============================================================================

/**
 * Reply moderation status values.
 * ARCHITECTURE REFERENCE: Section 5.2 - replies table
 */
export type ReplyModerationStatus = 'PENDING_REVIEW' | 'APPROVED' | 'REJECTED' | 'HIDDEN';

/**
 * Moderation action that can be performed on a reply.
 */
export type ReplyModerationAction = 'APPROVE' | 'REJECT' | 'HIDE';

/**
 * Admin reply list item (full data for moderation view).
 * Includes sensitive fields not shown in public responses.
 */
export interface AdminReplyListItem {
  replyId: string;
  questionId: string;
  body: string;
  moderationStatus: ReplyModerationStatus;
  authorName: string;
  authorEmail: string;
  userType: 'ADMIN' | 'CUSTOMER' | 'GUEST';
  guestToken: string | null;
  customerId: string | null;
  isSolution: boolean;
  replyType: string;
  createdAt: string;
  updatedAt: string;
  /**
   * Per-reply image context for the moderation view (ADR-022 §14
   * D-ADMIN-REPLY-DISPLAY / D5). Uses the ADMIN media shape
   * (`AdminMediaListItem`) — NOT the public `ImageAttachment` — because the
   * moderator must see EVERY state (a `MediaStatusBadge` needs
   * `moderationStatus`, which `ImageAttachment` lacks) and the `url` is the
   * authenticated `/api/admin/images/:id` byte path (fetched as a blob, never a
   * raw `<img src>`). Populated by the reply-list route from the UNFILTERED
   * admin batch read (never the §6.1 public predicate); `[]` when the reply has
   * no images. This same array feeds the RFI evidence picker.
   */
  images: AdminMediaListItem[];
}

/**
 * Query parameters for GET /api/admin/questions/:questionId/replies
 */
export interface AdminReplyFilters extends PaginationParams {
  status?: ReplyModerationStatus;
}

/**
 * Response for GET /api/admin/questions/:questionId/replies
 */
export interface AdminReplyListResponse {
  replies: AdminReplyListItem[];
  pagination: PaginationMeta;
}

/**
 * Request body for PATCH /api/admin/questions/:questionId/replies/:replyId
 */
export interface ModerateReplyRequest {
  /** Moderation action to perform */
  action: ReplyModerationAction;
  /** Reason for moderation (required for REJECT) */
  reason?: string;
}

/**
 * Response for PATCH /api/admin/questions/:questionId/replies/:replyId
 */
export interface ModerateReplyResponse {
  reply: {
    replyId: string;
    questionId: string;
    moderationStatus: ReplyModerationStatus;
    updatedAt: string;
  };
  /** Whether the moderation action was actually applied (false on idempotent no-op or CAS loss). */
  applied: boolean;
  sideEffects: {
    replyCountDelta: number;
    notificationScheduled: boolean;
  };
  message: string;
}

/**
 * Response for GET /api/admin/notifications/pending — the pending-work
 * contract (ADR-025 blueprint §3.2 / BL-ADMIN-PENDING-COUNTS-001).
 *
 * Every count is `store_hash`-scoped and computed from the SAME predicate its
 * destination admin-queue filter uses (identical predicate/unit/scope), so a
 * B-3 Overview tile can never disagree with the queue it navigates to.
 * The read is capability-gated (`PENDING_WORK_VIEW`, ext-SEC-01).
 */
export interface PendingNotificationsResponse {
  /**
   * Questions awaiting review (`status = 'PENDING_REVIEW'`, not deleted).
   * Unit: questions.
   */
  pendingQuestions: number;
  /**
   * DISTINCT non-deleted questions with >= 1 pending-review reply — the same
   * `needsReview` EXISTS predicate the admin queue filter uses.
   * Unit: DISTINCT questions (NOT replies).
   */
  questionsWithPendingReplies: number;
  /**
   * Actionable media-moderation work (`moderation_status = 'PENDING_REVIEW'`,
   * not deleted). Legal-held rows are outside this predicate by the
   * D-LEGALHOLD flow (a held row is forced HIDDEN). Unit: media items.
   */
  pendingMedia: number;
  /**
   * @deprecated Hard-coded `0` placeholder — never computed and never
   * rendered (OD-15: no RFI tile until a genuine staff-action predicate
   * exists). Kept emitted for wire compatibility; do not build UI on it.
   */
  unansweredRfi: number;
  /** Server timestamp (ISO-8601) at which the counts were computed. */
  asOf: string;
  /**
   * Age in whole seconds (relative to `asOf`) of the oldest item behind each
   * count, `null` when that count is 0 (OD-18 — age shown, no SLA claimed).
   * `questionsWithPendingReplies` measures the oldest pending-review reply
   * anchoring the counted set (when the oldest outstanding review work
   * arrived), not the parent question's creation time.
   *
   * Read consistency: each count/age pair is read as of its own query (no
   * cross-field snapshot) — a concurrent moderation between the reads can
   * transiently pair a non-zero question count with a `null` age (or the
   * inverse); self-heals on refresh (AW-R1-6). Consumers must tolerate
   * `null` regardless of the count. The media pair is immune (single
   * COUNT+MIN aggregate).
   */
  oldestItemAgeSeconds: {
    pendingQuestions: number | null;
    questionsWithPendingReplies: number | null;
    pendingMedia: number | null;
  };
}

// =============================================================================
// ADMIN MEDIA MODERATION TYPES (ADR-022 §10 / BL-IMG-ADMIN-MODERATION-001)
// =============================================================================
// The `MediaModerationStatus` / `MediaAuthorType` / `MediaRejectionReason` /
// `MediaModerationAction` unions are defined in `./constants` (derived from
// value arrays for runtime validation, the `AUDIT_ACTIONS` pattern) and
// imported above. They are deliberate client-side mirrors of the worker
// schema unions so the admin UI never imports the worker schema.

/**
 * Admin media review-queue list item (moderation view — includes admin-only
 * fields that must never reach a public DTO).
 */
export interface AdminMediaListItem {
  attachmentId: string;
  questionId: string | null;
  replyId: string | null;
  /**
   * Relative admin byte-serve URL (`/api/admin/images/:attachmentId`).
   * Fetch with an Authorization header (authenticated blob) — never usable as
   * a raw `<img src>`.
   */
  url: string;
  originalFilename: string | null;
  mimeType: string;
  fileSize: number;
  width: number | null;
  height: number | null;
  moderationStatus: MediaModerationStatus;
  uploadedByType: MediaAuthorType;
  /** Admin-only; never surfaced on poster-visible surfaces (anti-tip-off). */
  rejectionReason: MediaRejectionReason | null;
  moderatedBy: string | null;
  moderatedAt: string | null;
  publicVersion: number;
  /** True when the D-LEGALHOLD preservation hold is set (destructive paths vetoed). */
  legalHold: boolean;
  createdAt: string;
}

/**
 * Query parameters for GET /api/admin/media (the moderation review queue).
 */
export interface AdminMediaFilters extends PaginationParams {
  status?: MediaModerationStatus;
  questionId?: string;
  /**
   * `'question'` restricts to QUESTION-anchored rows (`reply_id IS NULL`) —
   * the question-gallery management scope (PR4b fold C1). Omitted → the full
   * moderation-queue scope (reply-anchored rows included).
   */
  anchor?: 'question';
}

/**
 * Response for GET /api/admin/media.
 */
export interface AdminMediaListResponse {
  media: AdminMediaListItem[];
  pagination: PaginationMeta;
  /**
   * Store-wide counts per moderation status (index-covered via
   * `idx_media_moderation`) for filter chips + the pending badge.
   */
  counts: Record<MediaModerationStatus, number>;
}

/**
 * Request body for PATCH /api/admin/media/:attachmentId.
 */
export interface ModerateMediaRequest {
  action: MediaModerationAction;
  /** Required for REJECT. Controlled vocabulary only — no free text. */
  reason?: MediaRejectionReason;
}

/**
 * Response for PATCH /api/admin/media/:attachmentId.
 */
export interface ModerateMediaResponse {
  media: {
    attachmentId: string;
    questionId: string | null;
    replyId: string | null;
    moderationStatus: MediaModerationStatus;
    publicVersion: number;
    moderatedAt: string | null;
    /**
     * True when the D-LEGALHOLD preservation hold is set on the row —
     * surfaced synchronously so a CAS-race loser can observe a concurrently
     * placed hold without waiting for the queue refresh (DSV4-F2).
     */
    legalHold: boolean;
  };
  /** Whether the action was actually applied (false on idempotent no-op or CAS loss). */
  applied: boolean;
  message: string;
}

/**
 * Request body for POST /api/admin/media/bulk — ONE action applied to a
 * bounded set of attachments (≤ MEDIA_MODERATION_BULK_MAX).
 */
export interface BulkModerateMediaRequest {
  attachmentIds: string[];
  action: MediaModerationAction;
  reason?: MediaRejectionReason;
}

/**
 * Per-item outcome for POST /api/admin/media/bulk (per-item transactionality —
 * a partial failure reports per-item results, ADR-022 §10(C)).
 */
export interface BulkModerateMediaItemResult {
  attachmentId: string;
  success: boolean;
  applied?: boolean;
  moderationStatus?: MediaModerationStatus;
  /** True when the D-LEGALHOLD preservation hold is set on the row. */
  legalHold?: boolean;
  error?: { code: string; message: string };
}

/**
 * Response for POST /api/admin/media/bulk.
 */
export interface BulkModerateMediaResponse {
  results: BulkModerateMediaItemResult[];
  summary: { total: number; succeeded: number; failed: number };
}

// =============================================================================
// ADMIN MEDIA AUTHORING TYPES (ADR-022 §10(A)/(B) / BL-IMG-ADMIN-AUTHORING-001)
// =============================================================================

/**
 * Response for the staff-authoring uploads (PR4b):
 *   POST /api/admin/questions/:questionId/images
 *   POST /api/admin/replies/:replyId/images
 *
 * Returns the full admin queue DTO so the UI can render the created row
 * (moderation state, admin byte-serve URL, dimensions) without a second
 * fetch. Staff uploads are auto-approved (`moderationStatus: 'APPROVED'`,
 * `uploadedByType: 'ADMIN'` — both SERVER-derived from the admin JWT; the
 * multipart body carries only the file).
 */
export interface AdminUploadImageResponse {
  media: AdminMediaListItem;
}

/**
 * Response for DELETE /api/admin/media/:attachmentId (PR4b — admin remove,
 * ADR-022 §10(B): soft-delete; R2 bytes are retained until the PR4-purge/PR7
 * executor; a legal-held row is never deletable — honest 409, D-LEGALHOLD).
 */
export interface AdminDeleteMediaResponse {
  media: {
    attachmentId: string;
    questionId: string | null;
    replyId: string | null;
    /** Moderation status the row held when it was removed. */
    previousStatus: MediaModerationStatus;
    isDeleted: true;
  };
  message: string;
}

/**
 * Read-only tenant media usage vs the D-QUOTA aggregate ceiling (ADR-022 §14
 * D-QUOTA — "admin usage surfacing lands with the §10.1 resolver"; the
 * tenant-tunable override is deferred, owner-approved 2026-07-04). Surfaced
 * on GET /api/admin/settings for the media settings card.
 */
export interface TenantMediaUsage {
  /** Total IMAGE rows for the tenant (ALL statuses, incl. soft-deleted — the D-QUOTA counting predicate). */
  attachments: number;
  /** Total stored canonical bytes for the tenant (same predicate). */
  bytes: number;
  /** Platform ceiling this usage counts against (TENANT_MEDIA_QUOTA.MAX_ATTACHMENTS). */
  maxAttachments: number;
  /** Platform ceiling this usage counts against (TENANT_MEDIA_QUOTA.MAX_TOTAL_BYTES). */
  maxBytes: number;
}

// =============================================================================
// ANALYTICS TYPES
// =============================================================================

/**
 * Single day data point for 7-day trend chart.
 * Used to render the sparkline visualization in the dashboard.
 */
export interface DailyQuestionCount {
  /** Date in UTC (ISO format: YYYY-MM-DD) */
  date: string;
  /** Number of questions created on this date */
  count: number;
}

/**
 * Status distribution for all question statuses.
 * Provides counts of questions in each status for the analytics dashboard.
 * All 7 statuses are always present (with 0 count if none exist).
 */
export interface StatusDistribution {
  /** Questions awaiting initial moderation */
  PENDING_REVIEW: number;
  /** Approved questions visible to public */
  OPEN: number;
  /** Questions being actively addressed */
  IN_PROGRESS: number;
  /** Questions needing additional information from submitter */
  NEEDS_MORE_INFO: number;
  /** Questions successfully answered/completed */
  RESOLVED: number;
  /** Questions closed as duplicates of existing questions */
  CLOSED_DUPLICATE: number;
  /** Questions closed/rejected for various reasons */
  CLOSED_REJECTED: number;
}

/**
 * Recent activity item for activity feed.
 * Represents a single question activity event in the dashboard.
 */
export interface RecentActivityItem {
  /** Unique question identifier (format: q_XXXXXXXXXXXX) */
  questionId: string;
  /** Question title (may be truncated in UI) */
  title: string;
  /** Current question status */
  status: string;
  /** Type of activity: created (new), updated (modified), or resolved */
  action: 'created' | 'updated' | 'resolved';
  /** When the activity occurred (ISO 8601 timestamp in UTC) */
  timestamp: string;
}

/**
 * Response for GET /api/admin/analytics
 *
 * PHASE_0C_SPEC.md: Task 0C-36
 */
export interface AnalyticsResponse {
  /** Questions submitted in the last 7 days (for sparkline chart) */
  questionsThisWeek: DailyQuestionCount[];
  /** Total questions this week */
  weeklyTotal: number;
  /** Average time from creation to resolution in hours (null if no resolved questions) */
  averageResponseTimeHours: number | null;
  /** Distribution of questions by status */
  statusDistribution: StatusDistribution;
  /** Last 10 recently updated questions */
  recentActivity: RecentActivityItem[];
}

// StoreSettings type should be imported from '@find-my-part/worker/db/schema' (Drizzle SSOT)
// See BRIEFING.md §1.2 for type source of truth guidelines

// =============================================================================
// ERROR CODES
// =============================================================================

export const ErrorCodes = {
  // General
  NOT_FOUND: 'NOT_FOUND',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  INTERNAL_ERROR: 'INTERNAL_ERROR',

  // Authentication
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  JWT_VERIFICATION_FAILED: 'JWT_VERIFICATION_FAILED',

  // Rate limiting
  RATE_LIMITED: 'RATE_LIMITED',

  // OAuth
  INVALID_OAUTH_PARAMS: 'INVALID_OAUTH_PARAMS',
  TOKEN_EXCHANGE_FAILED: 'TOKEN_EXCHANGE_FAILED',
  STORE_NOT_FOUND: 'STORE_NOT_FOUND',

  // State transitions (GAP-002)
  INVALID_STATE_TRANSITION: 'INVALID_STATE_TRANSITION',

  // Reply submission state (BL-REV-GAP006-001)
  QUESTION_NOT_ACCEPTING_REPLIES: 'QUESTION_NOT_ACCEPTING_REPLIES',
  REPLY_TYPE_NOT_SOLUTIONABLE: 'REPLY_TYPE_NOT_SOLUTIONABLE',

  // Features
  FEATURE_DISABLED: 'FEATURE_DISABLED',
  NOT_IMPLEMENTED: 'NOT_IMPLEMENTED',
} as const;

export type ErrorCode = (typeof ErrorCodes)[keyof typeof ErrorCodes];

// =============================================================================
// TAG TYPES (P1-23)
// =============================================================================

/**
 * Response for tag suggestion endpoints.
 */
export interface TagSuggestionResponse {
  suggestions: string[];
  topTags: string[];
}

/**
 * Tag taxonomy structure.
 */
export interface TagTaxonomy {
  windowTypes: string[];
  partCategories: string[];
  issueTypes: string[];
  brands: string[];
}

/**
 * Response for admin tag taxonomy endpoint.
 */
export interface AdminTagTaxonomyResponse {
  taxonomy: TagTaxonomy;
  allowedTags: string[];
}

/**
 * Response for public tag taxonomy endpoint.
 * Returns filtered taxonomy and all allowed tags.
 */
export interface PublicTagTaxonomyResponse {
  taxonomy: TagTaxonomy;
  allTags: string[];
}

// =============================================================================
// REPUTATION TYPES (P1-27)
// =============================================================================

/**
 * Badge detail information for display.
 */
export interface BadgeDetail {
  id: string;
  icon: string;
  name: string;
}

/**
 * Public-facing reputation data for a user.
 * Returned by GET /api/public/reputation.
 */
export interface PublicReputationResponse {
  displayName: string;
  points: number;
  badges: string[];
  badgeDetails: BadgeDetail[];
}

/**
 * Leaderboard entry for public and admin endpoints.
 */
export interface LeaderboardEntry {
  displayName: string;
  points: number;
  badges: string[];
  rank: number;
}

/**
 * Response for leaderboard endpoints.
 */
export interface LeaderboardResponse {
  leaderboard: LeaderboardEntry[];
}

/**
 * Admin reputation list item with full details.
 */
export interface AdminReputationUser {
  reputationId: string;
  displayName: string;
  guestToken: string | null;
  customerId: string | null;
  points: number;
  badges: string[];
  questionsSubmitted: number;
  repliesSubmitted: number;
  solutionsMarked: number;
  monthlyPoints: number;
  createdAt: string;
  updatedAt: string | null;
}

/**
 * Response for GET /api/admin/reputation.
 */
export interface AdminReputationListResponse {
  users: AdminReputationUser[];
  pagination: PaginationMeta;
}

// =============================================================================
// SETTINGS TYPES
// =============================================================================

/** Fields protected by the ADR-027 settings compare-and-set contract. */
export type StoreSettingsWriteField =
  | 'publicLaunchMode'
  | 'submissionAcknowledgementEnabled'
  | 'imageAttachmentsEnabled'
  | 'maxImagesPerSubmission'
  | 'maxImagesPerReply'
  | 'maxImagesPerStaffReply'
  | 'maxImageSizeMb'
  | 'allowedImageFormats'
  | 'imageModeration';

/**
 * Raw persisted values used as the settings write precondition. `null` means
 * the JSON path is absent (or contains JSON null); these values deliberately
 * do not pass through the launch/media default resolvers. Each value is
 * `unknown` because the token must faithfully carry a malformed legacy value
 * back to the Worker so an admin can repair it with a guarded write.
 */
export interface StoreSettingsExpectedState {
  publicLaunchMode: unknown;
  submissionAcknowledgementEnabled: unknown;
  imageAttachmentsEnabled: unknown;
  maxImagesPerSubmission: unknown;
  maxImagesPerReply: unknown;
  maxImagesPerStaffReply: unknown;
  maxImageSizeMb: unknown;
  allowedImageFormats: unknown;
  imageModeration: unknown;
}

/** Writable subset accepted by PUT /api/admin/settings. */
export type StoreSettingsPatch = Partial<{
  publicLaunchMode: PublicLaunchMode;
  submissionAcknowledgementEnabled: boolean;
  imageAttachmentsEnabled: boolean;
  maxImagesPerSubmission: number;
  maxImagesPerReply: number;
  maxImagesPerStaffReply: number;
  maxImageSizeMb: number;
  allowedImageFormats: AllowedMimeType[];
  imageModeration: ImageModerationPosture;
}>;

/** Token-aware settings request. Older clients may omit `expectedState`. */
export type UpdateStoreSettingsRequest = StoreSettingsPatch & {
  expectedState?: Partial<StoreSettingsExpectedState>;
};

/** Display-safe marker for an invalid raw settings value in a conflict response. */
export const SETTINGS_CONFLICT_MALFORMED_VALUE = 'malformed_raw_value' as const;

/**
 * Bounded values permitted in the public conflict contract. This is deliberately
 * distinct from `StoreSettingsExpectedState`: raw opaque values are valid CAS
 * tokens, but they must never be serialized or rendered as conflict evidence.
 */
export type StoreSettingsConflictValue =
  | PublicLaunchMode
  | boolean
  | number
  | AllowedMimeType[]
  | ImageModerationPosture
  | null
  | typeof SETTINGS_CONFLICT_MALFORMED_VALUE;

export type StoreSettingsConflictState = Partial<
  Record<StoreSettingsWriteField, StoreSettingsConflictValue>
>;

/** Machine-readable, bounded 409 payload for a rejected token-aware settings write. */
export interface StoreSettingsConflict {
  surface: 'settings';
  contested: StoreSettingsWriteField[];
  expected: StoreSettingsConflictState;
  observed: StoreSettingsConflictState;
  /**
   * `direct` means every observed value can safely become the next CAS token.
   * `refresh_required` means at least one raw observation was marker-encoded;
   * the client must fetch a fresh opaque token before any reapply attempt.
   */
  recovery: 'direct' | 'refresh_required';
}

/**
 * Store settings returned by GET /api/admin/settings.
 */
export interface StoreSettingsResponse {
  widgetEnabled: boolean;
  autoApproveLoggedIn: boolean;
  notificationEmail: string;
  maxImagesPerSubmission: number;
  allowedTags: string[];
  /**
   * Tenant public launch mode (Phase 4.5 controlled-launch slice). The
   * authoritative control for whether the public widget/page accepts customer
   * submissions. Always present and fail-closed (resolves to HIDDEN when
   * unset). See `PublicLaunchMode` in `./launchMode`.
   */
  publicLaunchMode: PublicLaunchMode;
  /**
   * Opt-in: send a single `SUBMISSION_RECEIVED` acknowledgement email after a
   * successful public question submission. Always present and fail-closed
   * (resolves to `false` when unset). Has no effect unless the resolved
   * `publicLaunchMode` is `VISIBLE` (public submissions are otherwise blocked).
   */
  submissionAcknowledgementEnabled: boolean;
  /**
   * ADR-022 §10.1 media settings (PR4b — BL-IMG-ADMIN-AUTHORING-001). All
   * values are EFFECTIVE (resolved + clamped via `resolveMediaSettings`) so
   * the admin card always displays what the server actually enforces —
   * a hand-set out-of-range persisted value reads back clamped.
   * `maxImagesPerSubmission` above is the persisted key for the §10.1
   * per-question cap (`maxImagesPerQuestion` — the resolver reads it).
   */
  imageAttachmentsEnabled: boolean;
  maxImagesPerReply: number;
  maxImagesPerStaffReply: number;
  maxImageSizeMb: number;
  allowedImageFormats: AllowedMimeType[];
  imageModeration: ImageModerationPosture;
  /**
   * Raw-stored compare-and-set token for the nine writable fields. Consumers
   * send back only the entries corresponding to fields in their patch.
   */
  expectedState?: StoreSettingsExpectedState;
  /**
   * Read-only tenant usage vs the D-QUOTA ceiling (ADR-022 §14 D-QUOTA usage
   * surfacing). Not writable — the ceiling is a platform constant.
   */
  mediaUsage: TenantMediaUsage;
  /**
   * EFFECTIVE (server-evaluated) release flags for this tenant (ADR-025
   * blueprint §3.5 / M17, delivered on the shared tenant-settings read — B-1).
   * Always present; every registered flag defaults OFF and fails closed
   * (`resolveReleaseFlags`), and the worker's global kill-switch is already
   * applied. Read-only on this contract — NOT writable via the settings PUT
   * (a flip is a server-side data change). Strictly separate control plane
   * from `publicLaunchMode` / entitlements / capabilities: a flag never gates
   * a security control.
   */
  releaseFlags: ReleaseFlags;
}

/** Minimal read-only projection for the Admin Workbench launch-status chip. */
export interface AdminLaunchStatusResponse {
  publicLaunchMode: PublicLaunchMode;
}

// =============================================================================
// PRODUCT TYPES (P1-30)
// =============================================================================

/**
 * Response for GET /api/admin/products/search.
 */
export interface ProductSearchResponse {
  products: SuggestedProduct[];
  total: number;
  hasMore: boolean;
}

/**
 * Response for GET /api/admin/products/:productIds.
 */
export interface ProductsByIdsResponse {
  products: SuggestedProduct[];
}
